import { useDragTaggingMutationKey } from "@/hooks/mutations/use-drag-tag";
import { useMainStore } from "@/stores/main";
import { useIsMutating } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { ModalFooterFull, ModalHeader } from "../common/modals";
import { useTableId } from "../mapping/hooks";
import { ModalWrapper } from "./modal-wrapper";

export default function DragTagModal({
  cellAddresses,
  koLabel,
  onConfirm,
  onCancel,
}: {
  cellAddresses?: string;
  koLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { data: tableId } = useTableId();
  const currentSelection = useMainStore((state) => state.selection);
  const mutationKey = useDragTaggingMutationKey(
    tableId?.tableId,
    currentSelection.current,
  );
  const isMutating = useIsMutating({ mutationKey: mutationKey });

  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;
  const isLoading = isMutating > 0;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter") return;
      if (e.isComposing) return;
      if (isLoading) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      onConfirmRef.current();
    };
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [isLoading]);

  if (!cellAddresses) return null;

  return (
    <ModalWrapper className="flex flex-col bg-white rounded-[10px] w-[425px] p-6 h-fit gap-4">
      <ModalHeader
        title="여러 셀 한 번에 태깅"
        description={`${cellAddresses}에 아래 태그를 태깅하시겠습니까?`}
        className="heading-1-semibold p-0"
      />

      <div className="flex flex-col px-4 py-3 border border-neutral-100 rounded-[4px]">
        <span className="body-1-normal-semibold text-neutral-600">
          {koLabel}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <span className="caption-1-regular text-neutral-500">
          * 태그는 덮어씌워집니다. <br />* 데이터 정보를 담고 있는 셀만 태깅되며
          헤더 정보를 담은 셀은 제외됩니다. <br />* 같은 주석목차의 셀들만
          태깅됩니다.
        </span>
      </div>

      <ModalFooterFull
        cancelLabel="취소"
        confirmLabel="확인"
        onConfirm={onConfirm}
        onCancel={onCancel}
        isLoading={isLoading}
      />
    </ModalWrapper>
  );
}
